import { useState } from 'react';
import { FileItem, FolderRef } from '../types';
import { useAuthV2 } from '../../../hooks/useAuthV2';
import { isPlottableFile, openAnalyticsWithFiles } from '../utils';
import { analyticsHandoffEntry, isDownloadableEntry } from '../childrenModel';
import { isWritableEntry } from '../writeTargets';
import type { UploadOutcome } from '../../file-uploader/uploadResults';

interface UseFileOperationsProps {
  /** The folder currently open, as a NODE (R-10). One field since B-4. */
  currentFolder: FolderRef;
  /**
   * The open folder's DISPLAY path, for the one consumer left: the upload's
   * `destination_path`. Passed alongside the ref rather than carried on it,
   * because it is a rendering datum and not an address — and inside a shared
   * folder it is share-root-relative, which is why the upload zone is withheld
   * there rather than sending it.
   */
  currentPath: string;
  onDownload: (entry: FileItem) => Promise<void>;
  onDelete: (entry: FileItem) => Promise<void>;
  onRenameFolder: (entry: FileItem, newName: string) => Promise<void>;
  onCreateFolder: (parent: FolderRef, name: string) => Promise<void>;
  onUpload: (
    files: File[],
    metadata: {
      destinationPath: string;
      flightType: string;
      payloadId: number;
      pilotId: string;
      customerId: string;
      projectId: string;
      airspaceObserverId: string;
      payloadOperatorId: string;
    }
  ) => Promise<UploadOutcome | void>;
  onShowDetails?: (file: FileItem) => void;
  /**
   * Open the file/folder-sharing dialog for a registry-backed entry — the one
   * Share action (STD-029 R-12). The per-file flight-share route retired in
   * B-6; the bulk drone-serial dialog on the Shared view still grants those.
   */
  onShareObject?: (file: FileItem) => void;
  onLinkFlight?: (file: FileItem) => void;
}

export const useFileOperations = ({
  currentFolder,
  currentPath,
  onDownload,
  onDelete,
  onRenameFolder,
  onCreateFolder,
  onUpload,
  onShowDetails,
  onShareObject,
  onLinkFlight,
}: UseFileOperationsProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedFile, setSelectedFile] = useState<FileItem | null>(null);
  const [showCreateFolder, setShowCreateFolder] = useState<boolean>(false);
  const [newFolderName, setNewFolderName] = useState<string>('');
  const [showRenameDialog, setShowRenameDialog] = useState<boolean>(false);
  const [renameTarget, setRenameTarget] = useState<FileItem | null>(null);
  const { profile } = useAuthV2();

  const handleContextMenu = (event: React.MouseEvent<HTMLElement>, file: FileItem) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedFile(file);
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
    setSelectedFile(null);
  };

  const handleDownload = async () => {
    // `isDownloadableEntry`, not `!isFolder`: an R-9 entry whose `s3_key` spells
    // no path has nothing to address either, and the menu hides the item for
    // exactly the same reason it hides it for a folder. Guarded here as well as
    // in the menu because this is the action and that is a rendering decision.
    if (selectedFile && isDownloadableEntry(selectedFile)) {
      try {
        // The ENTRY, not its key. A registry-backed row is presigned by id,
        // which is the only addressing that reaches a file inside a folder
        // somebody shared (B-4/D-6); an R-9 entry falls back to the path
        // endpoint, which is correct because it can only exist in the caller's
        // own tree.
        await onDownload(selectedFile);
      } catch (error) {
        console.error('Download failed:', error);
      }
    }
    closeMenu();
  };

  // One handler for both, because there is one endpoint for both: `DELETE
  // /api/file-objects/{id}` decides file-vs-folder from `blob_key IS NULL` on
  // the row, which is the only place that distinction is recorded (M-4). The
  // caller no longer has to pick a code path from `isFolder` and be right.
  //
  // Guarded on `isWritableEntry` even though the menu item is hidden without a
  // row: the menu is a rendering decision and this is the action, and an entry
  // with no `object_id` has nothing to address (R-9).
  const handleDelete = async () => {
    if (
      selectedFile &&
      isWritableEntry(selectedFile) &&
      confirm(`Delete ${selectedFile.isFolder ? 'folder' : 'file'} "${selectedFile.name}"?`)
    ) {
      try {
        await onDelete(selectedFile);
      } catch (error) {
        console.error('Delete failed:', error);
      }
    }
    closeMenu();
  };

  const handleRename = () => {
    if (selectedFile && selectedFile.isFolder && isWritableEntry(selectedFile)) {
      setRenameTarget(selectedFile);
      setShowRenameDialog(true);
    }
    closeMenu();
  };

  // Hand one file to Data Analysis Pro.
  //
  // The rule is `analyticsHandoffEntry` (pure, unit-tested): a foreign-owned
  // entry sends its id alone, an own entry keeps its path, and anything without
  // a resolvable address is withheld rather than handed over. It lives there
  // rather than here because getting it wrong is SILENT — a share-relative path
  // resolves in the caller's own namespace, and the bad case is not a 404 but
  // their own unrelated file being plotted on a screen about somebody else's
  // data.
  const handlePlot = () => {
    if (selectedFile && isPlottableFile(selectedFile.name)) {
      const payload = analyticsHandoffEntry(selectedFile, profile?.organization?.id);
      if (payload) {
        openAnalyticsWithFiles([payload], profile?.organization?.slug ?? null);
      }
    }
    closeMenu();
  };

  // Open the editable Flight Details dialog for a file linked to a flight.
  const handleShowDetails = () => {
    if (selectedFile && !selectedFile.isFolder && selectedFile.flight_id) {
      onShowDetails?.(selectedFile);
    }
    closeMenu();
  };

  // The one Share action (STD-029 R-12). Folders are explicitly allowed — a
  // folder share is the case the registry exists for — and the guard is
  // `object_id`, not `flight_id`: an entry served by the legacy S3 arm has no
  // row for a share to point at, while a file with no flight now shares
  // perfectly well (and one WITH a flight conveys that record along with the
  // bytes, per R-7). That is what let the flight-only route retire.
  const handleShareObject = () => {
    if (selectedFile?.object_id) {
      onShareObject?.(selectedFile);
    }
    closeMenu();
  };

  // "Link to flight..." (#1694). The menu decides when to offer it
  // (`canLinkToFlight`); this only hands the clicked entry over.
  const handleLinkFlight = () => {
    if (selectedFile?.object_id) {
      onLinkFlight?.(selectedFile);
    }
    closeMenu();
  };

  const handleRenameConfirm = async (newName: string) => {
    if (renameTarget) {
      try {
        // The ENTRY, not its key. The write needs `object_id`; the caller also
        // needs the old path, because a rename moves the display path of
        // everything below it and the open folder may be one of them (M-7).
        await onRenameFolder(renameTarget, newName);
      } catch (error) {
        console.error('Rename failed:', error);
      }
    }
    setShowRenameDialog(false);
    setRenameTarget(null);
  };

  const closeRenameDialog = () => {
    setShowRenameDialog(false);
    setRenameTarget(null);
  };

  const handleCreateFolder = async () => {
    if (newFolderName.trim()) {
      try {
        // A NAME and a PARENT NODE, never a concatenated path. Building
        // `${currentPath}/${name}` was how this became unable to create inside a
        // shared folder: that folder has no coordinate in the caller's own tree,
        // so there was no string to build. Name validation stays on the backend,
        // which mirrors the `file_objects_name_shape` CHECK.
        await onCreateFolder(currentFolder, newFolderName.trim());
        setNewFolderName('');
        setShowCreateFolder(false);
      } catch (error: unknown) {
        console.error('Create folder failed:', error);

        // Show user-friendly error message
        let errorMessage = 'Failed to create folder';
        if (error && typeof error === 'object') {
          if (
            'response' in error &&
            error.response &&
            typeof error.response === 'object' &&
            'data' in error.response
          ) {
            const data = error.response.data as { detail?: string };
            if (data.detail) {
              errorMessage = data.detail;
            }
          } else if ('message' in error && typeof error.message === 'string') {
            errorMessage = error.message;
          }
        }
        alert(`Failed to create folder: ${errorMessage}`);
      }
    }
  };

  const handleUploadWithPath = async (
    files: File[],
    metadata: {
      destinationPath: string;
      flightType: string;
      payloadId: number;
      pilotId: string;
      customerId: string;
      projectId: string;
      airspaceObserverId: string;
      payloadOperatorId: string;
    }
  ) => {
    // Auto-populate destination path with current path
    const updatedMetadata = {
      ...metadata,
      destinationPath: metadata.destinationPath || currentPath,
    };
    // Pass the outcome straight back — the uploader needs it to decide which
    // files stay selected after a part-succeeded batch (#1172).
    return onUpload(files, updatedMetadata);
  };

  const openCreateFolderDialog = () => {
    setShowCreateFolder(true);
  };

  const closeCreateFolderDialog = () => {
    setShowCreateFolder(false);
    setNewFolderName('');
  };

  return {
    // State
    anchorEl,
    selectedFile,
    showCreateFolder,
    newFolderName,
    showRenameDialog,
    renameTarget,

    // Handlers
    handleContextMenu,
    closeMenu,
    handleDownload,
    handleDelete,
    handleRename,
    handlePlot,
    handleShowDetails,
    handleShareObject,
    handleLinkFlight,
    handleRenameConfirm,
    closeRenameDialog,
    handleCreateFolder,
    handleUploadWithPath,
    openCreateFolderDialog,
    closeCreateFolderDialog,
    setNewFolderName,
  };
};
